/**
 * Individual user action dropdown component
 * Replaces bulk actions with per-user dropdown menus
 */

import React, { useState } from 'react';
import { AdminUser } from '../../services/admin';

export interface UserActionDropdownProps {
  user: AdminUser;
  onAction: (action: string, user: AdminUser) => void;
  disabled?: boolean;
}

export function UserActionDropdown({ user, onAction, disabled = false }: UserActionDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleActionClick = (action: string) => {
    setIsOpen(false);
    onAction(action, user);
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const actions = [
    {
      id: 'edit_profile',
      label: 'Edit Profile',
      icon: '',
      color: 'text-gray-200'
    },
    {
      id: 'generate_temp_password',
      label: 'Generate Temp Password',
      icon: '🔑',
      color: 'text-yellow-400'
    },
    {
      id: user.is_active ? 'deactivate' : 'activate',
      label: user.is_active ? 'Deactivate' : 'Activate',
      icon: user.is_active ? '🔴' : '✅',
      color: user.is_active ? 'text-red-400' : 'text-green-400'
    },
    {
      id: 'change_role',
      label: 'Change Role',
      icon: '👤',
      color: 'text-blue-400'
    },
    {
      id: 'delete',
      label: 'Delete User',
      icon: '🗑️',
      color: 'text-red-500'
    }
  ];

  return (
    <div className="relative" data-testid="user-action-dropdown">
      {/* Dropdown Button */}
      <button
        onClick={toggleDropdown}
        disabled={disabled}
        className={`
          flex items-center gap-1 px-3 py-1.5 text-sm rounded
          ${disabled 
            ? 'bg-gray-700 text-gray-500 cursor-not-allowed' 
            : 'bg-gray-800 text-white hover:bg-gray-700 cursor-pointer'
          }
          border border-gray-600 transition-colors
        `}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label={`Actions for user ${user.username}`}
      >
        <span>Actions</span>
        <span className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}>
          ▼
        </span>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div 
          className="absolute right-0 mt-1 w-48 bg-gray-800 border border-gray-600 rounded-md shadow-lg z-50"
          role="menu"
        >
          <div className="py-1">
            {actions.map((action) => (
              <button
                key={action.id}
                onClick={() => handleActionClick(action.id)}
                data-testid={`action-${action.id}`}
                className={`
                  w-full px-4 py-2 text-left text-sm transition-colors
                  hover:bg-gray-700 flex items-center gap-2
                  ${action.color}
                `}
                role="menuitem"
              >
                <span>{action.icon}</span>
                <span>{action.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Backdrop to close dropdown when clicking outside */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}
    </div>
  );
}
